import React, {Dispatch, FC, SetStateAction, useState} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'

import IconMinus from 'assets/icons/delete.svg'
import MobxTextInput from 'components/elements/fields/MobxTextInput'
import Tooltip from 'components/elements/Tooltip'
import PayStubEarningsItemStore from 'models/PayStub/PayStubEarningsItem.store'
import {usePayStubStore} from 'src/utils/contexts/RootStore.context'

import s from './FieldsPayStubEarningsStatement.module.scss'

const cx = cn.bind(s)

interface IItemEarningProps {
	store: PayStubEarningsItemStore

	shouldCalculate: boolean
	setShouldCalculate: Dispatch<SetStateAction<boolean>>
	setCalculateCount: Dispatch<SetStateAction<number>>
	cancel: () => void
}

const ItemEarning: FC<IItemEarningProps> = ({
	store,
	setCalculateCount,
	setShouldCalculate,
	shouldCalculate,
	cancel,
}) => {
	const payStubStore = usePayStubStore()
	const [defaultTotal, setDefaultTotal] = useState(store.total)
	const isReadOnly = payStubStore.isAutoCalculation

	const onFocus = () => {
		payStubStore.dirtyStepFive()
		payStubStore.pauseCalculation()
		setDefaultTotal(store.total)
		cancel()
	}

	/** Handles info to detect if should we triggers the calculation of the parent (PaystubItem) */
	const updateCalculationInfo = () => {
		payStubStore.resumeCalculation()
		if (store.total !== defaultTotal && payStubStore.isAutoCalculation) {
			setCalculateCount((n) => n + 1)
			setShouldCalculate(true)
		} else if (shouldCalculate) {
			setCalculateCount((n) => n + 1)
		}
	}

	return (
		<div className={cx('earnings__fields')} data-testid={`earnings-${store.key}`}>
			{store.isCustom ? (
				<div className={cx('earnings__field')}>
					<MobxTextInput
						placeholder="Enter here"
						type="text"
						store={store}
						name="name"
						variant="editable"
						onChange={(e) => payStubStore.setEarningsCustomName(e, store.id)}
					/>
				</div>
			) : (
				<div className={cx('earnings__field')}>
					<span className={cx('normal')}>{store.name}</span>
					{store.key === 'salary' && (
						<Tooltip
							className={cx('salary__tooltip')}
							text={
								<>
									<p>
										Please enter your annual rate in the Rate box. For example, if you make $52,000
										a year, please enter in 52,000.
									</p>
									<p>
										The total will be calculated by taking your annual rate divided by your
										selection of &quot;How often are you paid?&quot;. This total will be the total
										for this pay period and pay stub. For example, if you are paid $52,000 annually,
										and you have selected a weekly paystub, the total will be $52,000/52 weeks,
										which is $1,000 a week before taxes.
									</p>
									<p>
										The year to date will be the total multiplied by the amount of pay periods. If
										it is the 4th pay period, the year to date will be 4 times $1,000, which equals
										$4,000.
									</p>
								</>
							}
						/>
					)}
				</div>
			)}

			<div className={cx('earnings__field')}>
				<MobxTextInput
					className={cx('ta-right')}
					placeholder="0.00"
					store={store}
					name="rate"
					variant="editable"
					type="text"
					isFormatText
					shouldHandleOldValue
					isNumber
					onFocus={onFocus}
					onBlurCapture={updateCalculationInfo}
				/>
			</div>
			<div className={cx('earnings__field')}>
				<MobxTextInput
					className={cx('ta-right')}
					placeholder="0.00"
					store={store}
					name="hours"
					variant={store.key === 'tips' || store.key === 'salary' ? 'default' : 'editable'}
					type="text"
					isFormatText
					shouldHandleOldValue
					isNumber
					onFocus={onFocus}
					onBlurCapture={updateCalculationInfo}
					readOnly={store.key === 'tips' || store.key === 'salary'}
					disabled={store.key === 'tips' || store.key === 'salary'}
				/>
			</div>
			<div className={cx('earnings__field')}>
				<MobxTextInput
					className={cx('ta-right')}
					placeholder="0.00"
					store={store}
					name="total"
					type="text"
					isFormatText
					isNumber
					shouldHandleOldValue
					readOnly={isReadOnly}
					disabled={isReadOnly}
				/>
			</div>
			<div className={cx('earnings__field')}>
				<MobxTextInput
					className={cx('ta-right')}
					placeholder="0.00"
					store={store}
					name="ytd"
					type="text"
					isFormatText
					isNumber
					shouldHandleOldValue
					readOnly={isReadOnly}
					disabled={isReadOnly}
				/>
			</div>
			{store.isCustom && (
				<button
					className={cx('btn', 'btn--delete')}
					onClick={() => {
						payStubStore.removeEarningsItem(store.id)
						updateCalculationInfo()
					}}
					type="button"
					title="Remove earnings">
					<IconMinus />
				</button>
			)}
		</div>
	)
}

export default observer(ItemEarning)
